import { RecordSummaryPayload } from "@/types";

import { REVIEW_PROJECTION_MAX_RECORDS } from "../hooks/useTransformationRecords";

/**
 * How the review tab requests its forecast projection (#6755):
 *
 *   whole    — small enough to project in one go (the original behaviour)
 *   windowed — too large for that, but provably safe to project per window
 *   skipped  — too large AND not provably safe, OR the size is not yet known
 */
export type ProjectionStrategy = "whole" | "windowed" | "skipped";

/**
 * The ClaimantWindowIdentity definition this client knows how to trust —
 * mirrors Go's ClaimantWindowIdentityVersion (claimant_window_identity.go).
 * KEEP IN SYNC.
 *
 * The summary is persisted once at finalize; if the identity definition ever
 * changes, stored max_rows_per_claimant values are stale evidence that can
 * under-report. The server guard refuses such summaries; checking here too
 * turns that refusal into a graceful "skipped" (no forecast layer) instead of
 * a surfaced projection error.
 */
// v2 (#7568): the identity gained subscriber-id and relationship segments.
export const CLAIMANT_WINDOW_IDENTITY_VERSION = 2;

/**
 * Whether migration 000220's derived columns can be trusted for this
 * transformation — the client mirror of the server's
 * `requireRecordIndexAttributes` gate (#6893).
 *
 * The server answers a derived-column question with a **409** when the columns
 * are absent or were written under a superseded identity, because "no" and
 * "cannot tell" are indistinguishable in that data. Checking here too turns
 * that refusal into not asking: a request guaranteed to 409 on every
 * historical document would surface as an error state that looks like a server
 * fault.
 *
 * Lives beside the version constant on purpose. This predicate and the
 * MaxRowsPerClaimant gate below read the SAME two fields for different
 * questions, and splitting them across files is how one gets updated and the
 * other does not.
 */
export const derivedColumnsAvailable = (summary: RecordSummaryPayload | null | undefined): boolean =>
  summary?.identity_version === CLAIMANT_WINDOW_IDENTITY_VERSION;

interface ProjectionGateArgs {
  /** The transformation row's server-computed summary; absent pre-#6755. */
  summary: RecordSummaryPayload | null | undefined;
  /**
   * The windowed endpoint's whole-set count, or null while unknown (i.e.
   * before the first page's response). This is a server-side COUNT of the
   * child table, never a loaded-prefix length — a prefix length would read
   * "small" on page 1 of any document.
   */
  endpointTotal: number | null;
}

/**
 * Decide the projection strategy, failing closed on missing evidence.
 *
 * The whole-document projection materialises the transformation's ENTIRE
 * record set inside mgmt-api — 2.34 GiB measured on a 497,711-record document
 * against a 1Gi container (#6826) — so requesting it on a document whose size
 * we have not yet learned is how the review tab takes the API down for every
 * user. UNKNOWN SIZE MUST MEAN "skipped", not "small": the state that decides
 * initialises to 0, and 0 > threshold is false on exactly the render where
 * the transformation row has landed but no record page has (#6755 review).
 *
 * Size source, in order:
 *   1. `summary.total_records` — on the transformation row, so it is known on
 *      the same render as the transformation itself; no request can outrun it.
 *      Note it is the per-schema bucket SUM (a two-schema record counts
 *      twice), so it can only overstate — which errs toward "skipped", the
 *      safe direction.
 *   2. The windowed endpoint's total, once the first page lands. This is what
 *      keeps forecasts working on pre-#6755 transformations (no summary, no
 *      backfill migration): small ones project one page-round-trip later;
 *      large ones skip, because without a summary there is no
 *      max_rows_per_claimant and windowing cannot be proven safe.
 */
export const deriveProjectionStrategy = ({ summary, endpointTotal }: ProjectionGateArgs): ProjectionStrategy => {
  const size = summary?.total_records ?? endpointTotal;
  if (size === null || size === undefined) return "skipped";
  if (size <= REVIEW_PROJECTION_MAX_RECORDS) return "whole";
  // Windowing needs BOTH: one row per claimant, and proof the count was made
  // under the identity definition this client (and the server guard) trusts.
  // max_rows_per_claimant is null on the client mirror's abstention and can be
  // 0 on a real server summary ("no claimants at all") — === 1 fails closed on
  // the former and correctly declines to window a claimant-less document,
  // where a whole-doc-sized projection has nothing to forecast anyway.
  const windowSafe =
    summary?.max_rows_per_claimant === 1 && summary?.identity_version === CLAIMANT_WINDOW_IDENTITY_VERSION;
  return windowSafe ? "windowed" : "skipped";
};
